import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Map, OrderedMap } from 'immutable';

import TaxonomyLeydenTree from '@Containers/TreeView/Components/TaxonomyLeydenTree';
import { TAXOMONY_STATES } from '@Services/TreeView/TreeViewConstants';

/**
 * Recursively convert the Immutable OrderedMap thesaurus data to the plain JS
 * node array expected by TaxonomyLeydenTree.
 *
 * thesaurusGroupsForView is keyed by rootIds.join() for each group:
 *   'ROOT_ID'            → direct children of the root
 *   'ROOT_ID,item1_id'   → children of item1 (once lazy-loaded)
 */
function buildNodes(thesaurusGroupsForView, thesaurusGroupsPartiallySelectedMap, parentKey) {
  const items = thesaurusGroupsForView?.get(parentKey);
  if (!items?.size) return [];

  return items.toArray().map((item) => {
    const id = item.get('id');
    const childKey = item.get('rootIds')?.push(id).join();
    const children = buildNodes(thesaurusGroupsForView, thesaurusGroupsPartiallySelectedMap, childKey);
    const selected = item.get('selected');
    const partiallySelected = Boolean(thesaurusGroupsPartiallySelectedMap?.get(id));
    const hasChildren = item.get('hasChildren');

    let state;
    if (selected) state = TAXOMONY_STATES.SELECTED;
    else if (partiallySelected) state = TAXOMONY_STATES.HALFSELECTED;

    return {
      id,
      name: item.get('title'),
      hitCount: item.get('hitCount'),
      hasChildren,
      state,
      // Include loaded children; omit entirely for unloaded nodes so Leyden
      // renders a branch toggle driven by hasChildren alone.
      children: children.length ? children : undefined,
    };
  });
}

function ThesaurusGroupsRoot(props) {
  const { filter, thesaurusGroupsForView, thesaurusGroupsPartiallySelectedMap, onClickThesaurusItem, fetchChildren } = props;

  const thesaurusRootId = filter?.get('thesaurusRootId');

  // Reverse-lookup map: plain string id → Immutable item, for callback adapters.
  const itemMap = useMemo(() => {
    const map = {};
    thesaurusGroupsForView?.forEach((items) => {
      items?.forEach((item) => {
        const id = item.get('id');
        if (id != null) map[String(id)] = item;
      });
    });
    return map;
  }, [thesaurusGroupsForView]);

  const taxonomies = useMemo(() => {
    const rootSelected = filter?.get('selected');
    const rootPartiallySelected = Boolean(thesaurusGroupsPartiallySelectedMap?.get(thesaurusRootId));

    let rootState;
    if (rootSelected) rootState = TAXOMONY_STATES.SELECTED;
    else if (rootPartiallySelected) rootState = TAXOMONY_STATES.HALFSELECTED;

    return [
      {
        id: thesaurusRootId,
        name: filter?.get('title'),
        hitCount: filter?.get('hitCount'),
        hasChildren: true,
        state: rootState,
        children: buildNodes(thesaurusGroupsForView, thesaurusGroupsPartiallySelectedMap, thesaurusRootId),
      },
    ];
  }, [filter, thesaurusRootId, thesaurusGroupsForView, thesaurusGroupsPartiallySelectedMap]);

  // onSelect: TaxonomyLeydenTree passes a plain JS node; the existing Redux
  // action expects an Immutable item, so look it up from itemMap.
  const onSelect = useCallback(
    (node) => {
      if (String(node?.id) === String(thesaurusRootId)) {
        onClickThesaurusItem(filter);
        return;
      }
      const item = itemMap[String(node?.id)];
      if (item) onClickThesaurusItem(item);
    },
    [filter, thesaurusRootId, onClickThesaurusItem, itemMap]
  );

  // onLoad: called when a branch is expanded that has unloaded children.
  // Leyden manages expand state; we only need to trigger the fetch.
  const onLoad = useCallback(
    (node) => {
      // Root's children are loaded by syncThesaurusItemsWithGrouops on mount.
      if (String(node?.id) === String(thesaurusRootId)) return;
      const item = itemMap[String(node?.id)];
      if (!item) return;
      const status = item.get('childrenRetrievalStatus');
      if (status !== 'fetched' && status !== 'fetching') {
        fetchChildren(filter, item);
      }
    },
    [filter, thesaurusRootId, fetchChildren, itemMap]
  );

  if (!filter || !thesaurusRootId) return null;

  return (
    <TaxonomyLeydenTree
      taxonomies={taxonomies}
      onSelect={onSelect}
      onLoad={onLoad}
      ariaLabel={filter.get('title') || 'Filter lookup tree'}
      id="thesaurus-lookup-tree"
      showCountIndicator
    />
  );
}

ThesaurusGroupsRoot.propTypes = {
  filter: PropTypes.instanceOf(Map),
  thesaurusGroupsForView: PropTypes.instanceOf(OrderedMap),
  thesaurusGroupsPartiallySelectedMap: PropTypes.instanceOf(Map),
  onClickThesaurusItem: PropTypes.func.isRequired,
  fetchChildren: PropTypes.func.isRequired,
};

export default ThesaurusGroupsRoot;
